import React, { useEffect, useState } from "react";
import { Statement } from "../../firebase/statements";
import { Button, Grid, IconButton, Icon } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import {
  voteStatement,
  VoteType,
  getStatementVote,
  AGREE,
  DISAGREE,
  NO_OPINION,
} from "../../firebase/voting";
import { useAuth } from "../../firebase/AuthProvider";
import SignInDialog from "../signin/SigninDialog";
import AgreeIcon from "./svg/agree.svg";
import DisagreeIcon from "./svg/disagree.svg";
import { colors } from "../../theme";
import clsx from "clsx";
import { useParams, useHistory } from "react-router-dom";

interface Props {
  statement: Statement;
}

const useStyles = makeStyles((theme) => ({
  vote: {
    width: "100px",
    height: "100px",
    margin: "0px 20px 30px 20px",
    "&:hover": {
      opacity: "0.5",
    },
  },
  noopinion: {
    textTransform: "none",
    color: colors.VOTE_NO_OPINION_TEXT,
    backgroundColor: colors.VOTE_NO_OPINION_BG,
    height: "40px",
    width: "100px",
    fontSize: "16px",
    fontWeight: 600,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "-0.03px",
    "&:hover": {
      background: colors.VOTE_NO_OPINION_BG,
      opacity: "0.5",
    },
  },
  votedTheOtherOne: {
    opacity: "0.5",
    "&:hover": {
      opacity: "1",
    },
  },
}));

export default function Voting({ statement }: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { slug } = useParams();
  const history = useHistory();
  const { isSignedIn, user } = useAuth();
  const [vote, setVote] = useState<VoteType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openSignInDialog, setOpenSignInDialog] = useState(false);

  useEffect(() => {
    if (!user || !statement) return;
    const unsubscribe = getStatementVote(statement.id, user.id, setVote);
    return () => unsubscribe();
  }, [user, statement]);

  async function submitVote(vote: VoteType) {
    if (!isSignedIn || !user) {
      setOpenSignInDialog(true);
      return;
    }

    setIsLoading(true);
    await voteStatement(statement, user, vote);
    setIsLoading(false);

    // When there is no slug we can asume the user is on 
    // the dashboard page.
    // After voting redirect to Statement page
    if (slug === undefined && statement.slug) {
      return history.push(statement.slug); // <Redirect push to='/test'
    }
  }

  return (
    <>
      <SignInDialog
        open={openSignInDialog}
        onClose={() => setOpenSignInDialog(false)}
      />
      <Grid container item xs={12}>
        <Grid item xs={12} style={{ textAlign: "center" }}>
          <IconButton
            onClick={() => submitVote(AGREE)}
            disabled={isLoading}
            aria-label={t("voting.agree")}
            title={t("voting.agree")}
            style={{ backgroundColor: 'transparent' }}
          >
            <Icon
              className={clsx(
                classes.vote,
                vote && vote !== AGREE && classes.votedTheOtherOne
              )}
            >
              <img src={AgreeIcon} alt={t("voting.agree")} />
            </Icon>
          </IconButton>
          <IconButton
            onClick={() => submitVote(DISAGREE)}
            disabled={isLoading}
            aria-label={t("voting.disagree")}
            title={t("voting.disagree")}
            style={{ backgroundColor: 'transparent' }}
          >
            <Icon
              className={clsx(
                classes.vote,
                vote && vote !== DISAGREE && classes.votedTheOtherOne
              )}
            >
              <img src={DisagreeIcon} alt={t("voting.disagree")} />
            </Icon>
          </IconButton>
        </Grid>
        <Grid item xs={12} style={{ textAlign: "center" }}>
          <Button
            className={clsx(
              classes.noopinion,
              vote && vote !== NO_OPINION && classes.votedTheOtherOne
            )}
            onClick={() => submitVote(NO_OPINION)}
            disabled={isLoading}
            aria-label={t("voting.noopinion")}
            title={t("voting.noopinion")}
          >
            {t("voting.noopinion")}
          </Button>
        </Grid>
      </Grid>
    </>
  );
}
